import {useApolloClient, useReactiveVar} from '@apollo/client';
import {
  DisclaimerModal,
  ModalDisclaimersService,
} from '@vw-marketing/us-components';
import {InteractionLayerSizeV2} from '@volkswagen-onehub/layer-manager';
import _, {isArray, isNumber} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import {ErrorMessage} from '../../components/error-message';
import FilterAppliedBar from '../../components/filter-applied-bar';
import SearchFiltersSection from '../../components/search-filters-section';
import SearchPagination from '../../components/search-pagination';
import SearchResultsSection from '../../components/search-results-section';
import {useDisclaimerModule} from '../../hooks/use-disclaimer-module';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {useFeatureServices} from '../../hooks/use-feature-services';
import {useHistory} from '../../hooks/use-history';
import {
  FilterTracking,
  useTrackingManager,
} from '../../hooks/use-tracking-manager';
import {useTranslations} from '../../hooks/use-translations';
import {useZipData} from '../../hooks/use-zip-data';
import {searchData, userFilters} from '../../store/local-storage';
import {FILTERS} from '../../store/queries/filters';
import {USED_INVENTORY_DATA} from '../../store/queries/used-vehicles-inventory';
import {
  filterNameMap,
  onVWBasicFilterSearchResultPageload,
} from '../../tagging/tagging-helpers';
import {UserFilters} from '../../typing/local-storage';
import {AppStatus} from '../../typing/main';
import {FilterItem} from '../../typing/used-inventory';
import {
  DEFAULT_FILTERS_CONFIG,
  DEFAULT_RESULTS_PER_PAGE,
  MAPS_URL_TEMPLATE_RESULTS,
} from '../../utils/app-constants';
import {
  addAllFilterItems,
  addFilterItem,
  mergeFiltersData,
  removeAllFilterItems,
  removeFilterItem,
} from '../../utils/filters';
import {getDisclaimerKeys, scrollToDocumentTop} from '../../utils/general';
import {getStringifiedServiceConfigsServiceConfig} from '../../utils/get-stringified-service-configs-service-config';
import {
  setBreadcrumbs,
  updateUrlFilters,
  urlToFilters,
} from '../../utils/navigation-url';
import {renderLayerContent} from '../details-vehicle-page';
import {
  StyledSearchResultsPageMainContainer,
  StyledSearchFiltersContainer,
  StyledResultsSectionContainer,
  StyledSearchAppliedFilterContainer,
  StyledFiltersAppliedContainer,
} from './styles';
import SkeletonSearchResultsSection from '../../components/skeleton-search-results-section';

interface SearchResultsPageProps {}
const SearchResultsPage: React.FC<SearchResultsPageProps> = () => {
  const [appStatus, setAppStatus] = useState<AppStatus>('loading');
  const [startPageloadTracking, setStartPageloadTracking] = useState<boolean>(
    false,
  );
  let {
    resultsPerPage = DEFAULT_RESULTS_PER_PAGE,
    filterConfig = DEFAULT_FILTERS_CONFIG,
    disclaimers,
    faServicesUrl,
    mockIds,
    landingPageName,
    excludedModels,
  } = useFeatureAppConfig();
  const {filters: filtersTranslation} = useTranslations().resultsSection;
  filterConfig = {...DEFAULT_FILTERS_CONFIG, ...filterConfig};
  const modalDisclaimers = getDisclaimerKeys(disclaimers, useDisclaimerModule);
  const currentFilters = useReactiveVar(userFilters);
  const currentSearchData = useReactiveVar(searchData);
  const history = useHistory();
  const client = useApolloClient();
  const filtersToSkipOmit = useMemo(
    () => ['price', 'odometer', 'modelName'],
    [],
  );

  const {
    'zip-manager': zipManager,
    'layer-manager': layerManager,
    'service-config-provider': serviceConfigProvider,
    's2:server-request': serverRequest,
    'breadcrumb-service': breadcrumbService,
    'navigation-service': navigationService,
  } = useFeatureServices();
  const trackingManager = useTrackingManager();
  const serviceConfigsServiceConfig = getStringifiedServiceConfigsServiceConfig(
    serviceConfigProvider,
    serverRequest,
  );
  const zipcodeData = useZipData();
  const {contactDealer} = useTranslations().modals;
  const defaultObject = {
    ...currentFilters,
    filters: {
      price: filterConfig.price.default,
      odometer: filterConfig.mileage.default,
    },
    distance: filterConfig.distance.default,
    page: 0,
    sortBy: 'distance',
    filterByOnlineBuyingDealers: false,
    wasSearched: false,
  };

  useEffect(() => {
    currentFilters.wasSearched &&
      startPageloadTracking &&
      onVWBasicFilterSearchResultPageload(
        getTrackingFilterData(),
        trackingManager,
      );
    setStartPageloadTracking(false);
  }, [currentFilters.wasSearched]);

  const getTrackingFilterData = () => {
    const filterData = currentFilters?.filters;
    let filter: FilterTracking = {
      numberOfResults: currentSearchData?.totalVehicles,
      sortOrder: [currentFilters.sortBy],
    };

    filter.filterlist = [
      {
        type: 'single_value',
        name: filterNameMap.zip,
        values: [zipcodeData?.zipData?.zip],
      },
      {
        type: 'single_value',
        name: filterNameMap.distance,
        values: [
          currentFilters?.distance !== -1
            ? currentFilters?.distance
            : `${filterConfig.distance.maxDistance}+`,
        ],
      },
    ];

    filterData?.dealers?.length &&
      filter.filterlist.push({
        type: 'multi_value',
        name: filterNameMap.dealers,
        values: filterData.dealers,
      });

    filterData?.modelName?.length &&
      filter.filterlist.push({
        type: 'multi_value',
        name: filterNameMap.model,
        values: filterData.modelName,
      });

    filterData?.modelYear?.length &&
      filter.filterlist.push({
        type: 'multi_value',
        name: filterNameMap.year,
        values: filterData.modelYear,
      });

    filter.filterlist.push(
      {
        type: 'range',
        name: filterNameMap.price,
        lowerbound: (filterData?.price?.length
          ? filterData?.price[0] !== -1
            ? filterData?.price[0]
            : filterConfig.price.minDistance
          : filterConfig.price.minDistance
        )?.toString(),
        upperbound: (filterData?.price?.length
          ? filterData?.price[1] !== -1
            ? filterData?.price[1]
            : `${filterConfig.price.maxDistance}+`
          : filterConfig.price.maxDistance
        )?.toString(),
      },
      //More filters section
      {
        type: 'range',
        name: filterNameMap.mileage,
        lowerbound: (filterData?.odometer?.length
          ? filterData?.odometer[0] !== -1
            ? filterData?.odometer[0]
            : filterConfig.mileage.minDistance
          : filterConfig.mileage.minDistance
        )?.toString(),
        upperbound: (filterData?.odometer?.length
          ? filterData?.odometer[1] !== -1
            ? filterData?.odometer[1]
            : `${filterConfig.mileage.maxDistance}+`
          : filterConfig.mileage.maxDistance
        )?.toString(),
      },
    );

    filterData?.transmissionType?.length &&
      filter.filterlist.push({
        type: 'multi_value',
        name: filterNameMap.transmissionType,
        values: filterData.transmissionType,
      });

    filterData?.fuelType?.length &&
      filter.filterlist.push({
        type: 'multi_value',
        name: filterNameMap.fuelType,
        values: filterData.fuelType,
      });

    filterData?.exteriorColor?.length &&
      filter.filterlist.push({
        type: 'multi_value',
        name: filterNameMap.exteriorColor,
        values: filterData.exteriorColor,
      });

    filterData?.interiorColor?.length &&
      filter.filterlist.push({
        type: 'multi_value',
        name: filterNameMap.interiorColor,
        values: filterData.interiorColor,
      });

    return filter;
  };

  const getAllFiltersData = async (zipCode: string) => {
    const filtersData = await client.query({
      query: FILTERS,
      variables: {
        zipcode: zipCode,
        distance: filterConfig.distance.plusValue,
      },
    });

    const newExcludedModelsArr = excludedModels?.map(item => item.id);
    const filterModel = filtersData.data.usedFilter.modelName?.filter(
      item => !newExcludedModelsArr?.includes(item),
    );

    const newFilterData = {
      usedFilter: {
        ...filtersData.data.usedFilter,
        modelName: filterModel,
      },
    };

    return newFilterData;
  };

  const getCurrentData = async (newFilters?: UserFilters) => {
    const filtersData = await client.query({
      query: USED_INVENTORY_DATA,
      variables: {
        distance: newFilters?.distance,
        filters: JSON.stringify(newFilters?.filters || {}),
        pageNumber: newFilters?.page,
        pageSize: resultsPerPage,
        sortBy: newFilters?.sortBy || '',
        zipcode: zipcodeData.zipData.zip,
        mapsUrlTemplate: MAPS_URL_TEMPLATE_RESULTS,
      },
    });
    return filtersData.data;
  };

  const onHandlePageChange = (pageNumber: number) => {
    const newFilter = {...currentFilters, page: pageNumber, wasSearched: false};
    userFilters(newFilter);
    updateUrlFilters(history, newFilter);
    scrollToDocumentTop();
  };

  const onHandleSortChange = (selectedOption?: string) => {
    const newFilter = {
      ...currentFilters,
      sortBy: selectedOption,
      page: 0,
      wasSearched: false,
    };
    userFilters(newFilter);
    updateUrlFilters(history, newFilter, false, filtersToSkipOmit);
    scrollToDocumentTop();
  };

  const onHandleFilterChange = (item: FilterItem) => {
    let updatedFilters = {...currentFilters, wasSearched: false};
    if (item.isChecked) {
      updatedFilters = removeFilterItem(item, updatedFilters);
    } else {
      updatedFilters = addFilterItem(item, updatedFilters);
    }
    updatedFilters = {...updatedFilters, page: 0};
    userFilters(updatedFilters);
    updateUrlFilters(history, updatedFilters);
    scrollToDocumentTop();
  };

  const onHandleFilterAllSelection = (
    items: FilterItem[],
    isChecked: boolean,
  ) => {
    let updatedFilters = {...currentFilters, wasSearched: false};
    if (isChecked) {
      updatedFilters = addAllFilterItems(items, updatedFilters);
    } else {
      updatedFilters = removeAllFilterItems(items, updatedFilters);
    }
    updatedFilters = {...updatedFilters, page: 0};
    userFilters(updatedFilters);
    updateUrlFilters(history, updatedFilters);
    scrollToDocumentTop();
  };

  const onHandleSliderFilterChange = (
    item: number | number[],
    type: string,
  ) => {
    let updatedFilters = {...currentFilters, wasSearched: false};
    switch (type) {
      case 'distance': {
        if (isNumber(item)) {
          if (item >= filterConfig.distance.maxDistance)
            item = filterConfig.distance.plusValue;

          updatedFilters = {...updatedFilters, distance: item, page: 0};
        }
        break;
      }
      case 'price': {
        if (isArray(item)) {
          updatedFilters = {
            ...updatedFilters,
            filters: {...updatedFilters.filters, price: item},
            page: 0,
          };
        }
        break;
      }
      case 'mileage': {
        if (isArray(item)) {
          updatedFilters = {
            ...updatedFilters,
            filters: {...updatedFilters.filters, odometer: item},
            page: 0,
          };
        }
        break;
      }
      default:
        break;
    }
    userFilters(updatedFilters);
    updateUrlFilters(history, updatedFilters);
    scrollToDocumentTop();
  };

  const onHandleClearAllFilters = () => {
    const updatedFilters = defaultObject;
    userFilters(updatedFilters);
    updateUrlFilters(history, updatedFilters);
    scrollToDocumentTop();
  };

  const onHandleZipChange = () => {
    if (!zipcodeData.zipData.zipError) onHandleClearAllFilters();
    scrollToDocumentTop();
  };

  const onHandleContactDealer = () => {
    layerManager.openInteractionLayer(
      renderLayerContent,
      {
        text: 'results interaction layer',
        index: 4,
        vehicle: {dealer: currentSearchData?.closestDealer},
        formType: 'contact-dealer',
        formTranslation: contactDealer,
        zipData: zipcodeData.zipData,
        serverConfig: {
          faServicesUrl,
          mockIds,
          serviceConfigsServiceConfig,
          trackingManager,
        },
      },
      {size: InteractionLayerSizeV2.C},
    );
  };

  useEffect(() => {
    const zipManagerUnsubscribe = zipManager.subscribe(onHandleZipChange);
    const newFilter = urlToFilters(history, defaultObject);
    if (
      !_.isEqual(
        _.omit(newFilter, 'wasSearched'),
        _.omit(currentFilters, 'wasSearched'),
      )
    ) {
      userFilters({...currentFilters, ...newFilter, wasSearched: false});
    }
    breadcrumbService &&
      setBreadcrumbs(
        history.location,
        history,
        undefined,
        breadcrumbService,
        navigationService,
        landingPageName,
      );
    return () => {
      zipManagerUnsubscribe();
    };
  }, []);

  useEffect(() => {
    if (currentFilters.wasSearched) return setAppStatus('loaded');
    setAppStatus('loading');
    const allFiltersQuery = getAllFiltersData(zipcodeData.zipData.zip);
    const searchDataQuery = getCurrentData(currentFilters);
    Promise.all([allFiltersQuery, searchDataQuery])
      .then(([allFilterDataResponse, searchDataResponse]: any[]) => {
        if (
          !_.isEmpty(allFilterDataResponse) &&
          !_.isEmpty(searchDataResponse)
        ) {
          const mergedFilters = mergeFiltersData(
            allFilterDataResponse.usedFilter,
            currentFilters,
            searchDataResponse.usedInventory,
            filterConfig,
            filtersTranslation,
          );
          searchData({
            ...searchDataResponse.usedInventory,
            appFilters: mergedFilters,
          });
        }
        setAppStatus('loaded');
        setStartPageloadTracking(true);
      })
      .catch(error => {
        console.error('Error loading data:', {error, message: error.message});
        setAppStatus('error');
      })
      .finally(() => {
        userFilters({...currentFilters, wasSearched: true});
      });
  }, [currentFilters]);

  if (appStatus === 'error') {
    return <ErrorMessage message1={'Error loading inventory data'} />;
  }

  return (
    <div>
      <ModalDisclaimersService disclaimerData={{active: false}}>
        <StyledSearchResultsPageMainContainer className="search-results_main-container">
          <StyledSearchFiltersContainer className="search-results_filters-container">
            <div className="search-filter_wrapper">
              <SearchFiltersSection
                filters={{
                  ...currentSearchData?.appFilters,
                  distance: currentFilters?.distance,
                  price: currentFilters.filters?.price,
                  odometer: currentFilters.filters?.odometer,
                }}
                onDeleteAllElements={onHandleClearAllFilters}
                onDeleteElement={onHandleFilterChange}
                onHandleClearAllFilters={onHandleClearAllFilters}
                onHandleFilterChange={onHandleFilterChange}
                onHandleSelectAllFilter={onHandleFilterAllSelection}
                onHandleSliderChange={onHandleSliderFilterChange}
                onHandleSortChange={onHandleSortChange}
                sortByValue={currentFilters.sortBy || ''}
                totalPages={currentSearchData?.totalPages || 0}
                totalVehicles={currentSearchData?.totalVehicles}
              />
            </div>
          </StyledSearchFiltersContainer>
          <StyledFiltersAppliedContainer>
            {!!currentSearchData?.appFilters?.appliedFilters?.length && (
              <StyledSearchAppliedFilterContainer className="search-results_applied-filter-container">
                <FilterAppliedBar
                  onDeleteElement={onHandleFilterChange}
                  onDeleteAllElements={onHandleClearAllFilters}
                  filters={currentSearchData?.appFilters?.appliedFilters}
                />
              </StyledSearchAppliedFilterContainer>
            )}
          </StyledFiltersAppliedContainer>
          <StyledResultsSectionContainer className="search-results_results-section-container">
            <SearchPagination
              currentPage={currentFilters.page}
              distance={currentFilters?.distance?.toString() || ''}
              loadingStatus={appStatus}
              onHandlePageChange={onHandlePageChange}
              onHandleSortChange={onHandleSortChange}
              pageSize={resultsPerPage}
              sortByValue={currentFilters.sortBy || ''}
              totalPages={currentSearchData?.totalPages || 0}
              totalVehicles={currentSearchData?.totalVehicles || 0}
            >
              {appStatus === 'loaded' ? (
                <SearchResultsSection
                  currentSearchData={currentSearchData}
                  disclaimers={modalDisclaimers}
                  onHandleContactDealer={onHandleContactDealer}
                />
              ) : appStatus === 'loading' ? (
                <SkeletonSearchResultsSection />
              ) : (
                undefined
              )}
            </SearchPagination>
          </StyledResultsSectionContainer>
        </StyledSearchResultsPageMainContainer>
        <DisclaimerModal disclaimers={modalDisclaimers} />
      </ModalDisclaimersService>
    </div>
  );
};

export default SearchResultsPage;
